import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { CompanySelectionService } from '@core/service/company-selection.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { DateTimePickerFieldComponent } from '@shared/components/date-time-picker-field/date-time-picker-field.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { OrgUnitTreeSelectComponent } from '@shared/components/org-unit-tree-select/org-unit-tree-select.component';
import { CompanySelectComponent } from '@shared/components/company-select/company-select.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import { EmployeeRecord } from '../../organization/employees/employee.interface';
import { AttendanceEventRecord } from '../attendance-event/attendance-event.interface';
import { AttendanceResultListResponse, AttendanceResultRecord } from '../attendance-result/attendance-result.interface';
import { clearAttendanceWeekFilterState, persistAttendanceWeekFilterState, restoreAttendanceWeekFilterState } from '../attendance-week-filter-state.util';
import { MissingPunchDialogComponent, MissingPunchDialogResult, MissingPunchSlot } from './missing-punch-dialog.component';

interface OrgUnitRecord { orgUnitId: string; companiesRecordId: string; name: string; parentId?: string | null; type?: string | null; orgPath?: string | null; isActive?: boolean; }
interface CollectionResponse<T> { data?: T[]; items?: T[]; results?: T[]; companiesRecords?: T[]; orgUnits?: T[]; employees?: T[]; }
type PunchSlot = MissingPunchSlot;
interface MissingPunchRow {
  result: AttendanceResultRecord;
  employeeName: string;
  employeeNumber: string;
  orgUnitName: string;
  punches: Partial<Record<PunchSlot, AttendanceEventRecord>>;
  missing: PunchSlot[];
  eventsResolved: boolean;
}

@Component({
  selector: 'app-missing-punch',
  standalone: true,
  imports: [CommonModule, FormsModule, BreadcrumbComponent, DateTimePickerFieldComponent, LoadingStateComponent, OrgUnitTreeSelectComponent, CompanySelectComponent,
    MatAutocompleteModule, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule,
    MatPaginatorModule, MatSelectModule, TranslateModule],
  templateUrl: './missing-punch.component.html',
  styleUrl: './missing-punch.component.scss',
})
export class MissingPunchComponent implements OnInit {
  private static readonly FILTER_STATE_KEY = 'attendance.missing-punches.weekly-filters.v1';
  companies: CompaniesRecord[] = [];
  orgUnits: OrgUnitRecord[] = [];
  employees: EmployeeRecord[] = [];
  rows: MissingPunchRow[] = [];
  isLoading = true;
  currentPage = 1;
  pageSize = 25;
  totalItems = 0;
  employeeSearchText: string | EmployeeRecord = '';
  filters = { companiesRecordId: '', orgUnitId: '', employeeId: '', weekDate: this.toDateValue(new Date()) };

  private readonly api = inject(AuthorizedApiService);
  private readonly authorization = inject(AuthorizationService);
  private readonly companySelection = inject(CompanySelectionService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly eventCache = new Map<string, AttendanceEventRecord | null>();
  private requestId = 0;
  private employeeSearchHandle: ReturnType<typeof setTimeout> | null = null;

  get canCreatePunch(): boolean { return this.authorization.hasAccess({ resource: 'attendanceEvents', action: 'create' }); }
  readonly timelineSlots: ReadonlyArray<{ slot: PunchSlot; labelKey: string }> = [
    { slot: 'entry', labelKey: 'entry' },
    { slot: 'meal_start', labelKey: 'mealStart' },
    { slot: 'meal_end', labelKey: 'mealEnd' },
    { slot: 'exit', labelKey: 'exit' },
  ];

  ngOnInit(): void { this.restoreState(); void this.initialize(); }
  get selectedEmployee(): EmployeeRecord | null { return this.employees.find((item) => item.employeeId === this.filters.employeeId) || null; }
  readonly displayEmployee = (value: string | EmployeeRecord | null): string => !value ? '' : typeof value === 'string' ? value : `${value.fullName || value.employeeNumber} · ${value.employeeNumber}`;

  async initialize(): Promise<void> {
    this.isLoading = true;
    try { await this.loadCompanies(); await Promise.all([this.loadOrgUnits(), this.loadEmployees()]); await this.loadRows(); }
    finally { this.isLoading = false; }
  }
  async applyFilters(): Promise<void> { this.currentPage = 1; this.persistState(); await this.loadRows(); }
  async refresh(): Promise<void> { this.eventCache.clear(); await this.loadRows(); }
  async clearFilters(): Promise<void> {
    this.filters = { companiesRecordId: this.companies.length === 1 ? this.companySelection.resolveSelection(this.companies) : '', orgUnitId: '', employeeId: '', weekDate: this.toDateValue(new Date()) };
    this.companySelection.rememberSelection(this.filters.companiesRecordId);
    this.employeeSearchText = ''; this.currentPage = 1;
    clearAttendanceWeekFilterState(MissingPunchComponent.FILTER_STATE_KEY);
    await Promise.all([this.loadOrgUnits(), this.loadEmployees()]); await this.loadRows();
  }
  async changeWeek(offset: number): Promise<void> {
    const date = this.parseDate(this.filters.weekDate) || new Date(); date.setDate(date.getDate() + offset * 7);
    this.filters.weekDate = this.toDateValue(date); await this.applyFilters();
  }
  async onWeekDateChanged(value: string): Promise<void> { if (!value || value === this.filters.weekDate) return; this.filters.weekDate = value; await this.applyFilters(); }
  async onCompanyChanged(): Promise<void> {
    this.companySelection.rememberSelection(this.filters.companiesRecordId);
    this.filters.orgUnitId = ''; this.filters.employeeId = ''; this.employeeSearchText = '';
    await Promise.all([this.loadOrgUnits(), this.loadEmployees()]); await this.applyFilters();
  }
  async onOrgUnitChanged(): Promise<void> { this.filters.employeeId = ''; this.employeeSearchText = ''; await this.loadEmployees(); await this.applyFilters(); }
  onEmployeeInput(value: string | EmployeeRecord): void {
    this.employeeSearchText = value; const text = this.displayEmployee(value);
    if (this.selectedEmployee && text !== this.displayEmployee(this.selectedEmployee)) { this.filters.employeeId = ''; void this.applyFilters(); }
    if (this.employeeSearchHandle) clearTimeout(this.employeeSearchHandle);
    this.employeeSearchHandle = setTimeout(() => void this.loadEmployees(text), 250);
  }
  onEmployeeSelected(event: MatAutocompleteSelectedEvent): void {
    const employee = event.option.value as EmployeeRecord; this.filters.employeeId = employee.employeeId;
    this.employeeSearchText = employee; void this.applyFilters();
  }
  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1; this.pageSize = event.pageSize; this.persistState(); void this.loadRows();
  }
  viewResult(row: MissingPunchRow): void { void this.router.navigate(['/attendance/attendance-results', row.result.attendanceResultId]); }
  addMissingPunch(row: MissingPunchRow, slot: PunchSlot): void {
    if (!this.canCreatePunch || !this.isMissing(row, slot)) return;
    const dialogRef = this.dialog.open<MissingPunchDialogComponent, unknown, MissingPunchDialogResult | null>(MissingPunchDialogComponent, {
      width: 'min(720px, calc(100vw - 24px))', maxWidth: '720px', maxHeight: 'calc(100dvh - 24px)',
      autoFocus: false, restoreFocus: true, disableClose: true, panelClass: ['missing-punch-dialog-panel'],
      data: { result: row.result, employeeName: row.employeeName, employeeNumber: row.employeeNumber, slot, punches: row.punches },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;
      this.eventCache.set(result.event.attendanceEventId, result.event);
      this.snackBar.open(
        this.t(result.recalculationQueued ? 'missingPunchesCatalog.manualPunch.success' : 'missingPunchesCatalog.manualPunch.successWithoutRecalculation'),
        undefined,
        { duration: 4200, panelClass: [result.recalculationQueued ? 'snackbar-success' : 'snackbar-warning'], horizontalPosition: 'center', verticalPosition: 'bottom' },
      );
      void this.refreshAfterCreation();
    });
  }
  punchTime(row: MissingPunchRow, slot: PunchSlot): string {
    const event = row.punches[slot]; if (!event?.eventAt) return '';
    const date = new Date(event.eventAt); if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(this.locale, { hour: '2-digit', minute: '2-digit', hour12: false, ...(row.result.timezone ? { timeZone: row.result.timezone } : {}) }).format(date);
  }
  isMissing(row: MissingPunchRow, slot: PunchSlot): boolean { return row.missing.includes(slot); }
  incidentLabel(row: MissingPunchRow): string {
    if (!row.missing.length) return this.t('missingPunchesCatalog.incidents.generic');
    return row.missing.map((slot) => this.t(`missingPunchesCatalog.incidents.${slot}`)).join(' · ');
  }

  private async loadRows(): Promise<void> {
    const requestId = ++this.requestId; this.isLoading = true;
    const { from, to } = this.weekBounds();
    try {
      const response = await firstValueFrom(this.api.get<AttendanceResultListResponse>('attendance-results', { params: {
        status: 'incomplete', hasMissingPunch: 'true', isActive: 'true', workDateFrom: from, workDateTo: to,
        page: this.currentPage, perPage: this.pageSize, sort: ['-workDate', 'employeeName:asc'],
        ...(this.filters.employeeId ? { employeeId: this.filters.employeeId } : {}),
        ...(this.filters.companiesRecordId ? { companiesRecordId: this.filters.companiesRecordId } : {}),
        ...(this.filters.orgUnitId ? { orgUnitId: this.filters.orgUnitId } : {}),
      } }));
      if (requestId !== this.requestId) return;
      const results = response?.data || [];
      await this.resolveEventIds(results.flatMap((item) => item.eventIds || []));
      if (requestId !== this.requestId) return;
      this.rows = results.map((result) => this.buildRow(result));
      this.totalItems = Number(response?.pagination?.total || 0);
    } catch (error) {
      if (requestId !== this.requestId) return;
      this.rows = []; this.totalItems = 0;
      this.snackBar.open(extractApiErrorMessage(error, this.t('missingPunchesCatalog.loadError')), undefined,
        { duration: 4000, panelClass: ['snackbar-danger'], horizontalPosition: 'center', verticalPosition: 'bottom' });
    } finally { if (requestId === this.requestId) this.isLoading = false; }
  }
  private async refreshAfterCreation(): Promise<void> {
    await this.refresh();
    setTimeout(() => void this.refresh(), 1800);
  }
  private buildRow(result: AttendanceResultRecord): MissingPunchRow {
    const embedded = this.embeddedEvents(result);
    const cached = (result.eventIds || []).map((id) => this.eventCache.get(id)).filter((item): item is AttendanceEventRecord => !!item);
    const events = [...embedded, ...cached].sort((a, b) => `${a.eventAt}`.localeCompare(`${b.eventAt}`));
    const punches: Partial<Record<PunchSlot, AttendanceEventRecord>> = {};
    events.forEach((event) => { const slot = this.eventSlot(event); if (!slot) return; if (!punches[slot] || slot === 'exit' || slot === 'meal_end') punches[slot] = event; });
    const missing = this.missingSlots(result, punches, events.length > 0);
    const employee = this.employees.find((item) => item.employeeId === result.employeeId);
    return { result, punches, missing, eventsResolved: events.length > 0,
      employeeName: result.employeeName || employee?.fullName || result.employeeNumber || '—',
      employeeNumber: result.employeeNumber || employee?.employeeNumber || '—',
      orgUnitName: this.orgUnits.find((unit) => unit.orgUnitId === result.orgUnitId)?.name || employee?.orgUnitName || '—' };
  }
  private missingSlots(result: AttendanceResultRecord, punches: Partial<Record<PunchSlot, AttendanceEventRecord>>, resolved: boolean): PunchSlot[] {
    const codes = (result.incidentCodes || []).map((code) => code.toLowerCase()); const explicit: PunchSlot[] = [];
    const matches = (words: string[]): boolean => codes.some((code) => words.every((word) => code.includes(word)));
    if (matches(['missing', 'entry']) || matches(['falta', 'entrada'])) explicit.push('entry');
    if (matches(['missing', 'exit']) || matches(['falta', 'salida'])) explicit.push('exit');
    if (matches(['missing', 'meal', 'start']) || matches(['missing', 'break', 'start'])) explicit.push('meal_start');
    if (matches(['missing', 'meal', 'end']) || matches(['missing', 'break', 'end'])) explicit.push('meal_end');
    if (explicit.length) return [...new Set(explicit)];
    if (!resolved) return [];
    const missing: PunchSlot[] = [];
    if (!punches.entry) missing.push('entry');
    if (!punches.exit) missing.push('exit');
    if (punches.meal_start && !punches.meal_end) missing.push('meal_end');
    if (punches.meal_end && !punches.meal_start) missing.push('meal_start');
    return missing;
  }
  private eventSlot(event: AttendanceEventRecord): PunchSlot | null {
    const type = `${event.normalizedEventType || event.eventType || ''}`.toLowerCase();
    if (['entry', 'check_in', 'clock_in', 'in'].includes(type)) return 'entry';
    if (['exit', 'check_out', 'clock_out', 'out'].includes(type)) return 'exit';
    if (['meal_start', 'break_start', 'lunch_start', 'meal_out'].includes(type)) return 'meal_start';
    if (['meal_end', 'break_end', 'lunch_end', 'meal_in'].includes(type)) return 'meal_end';
    return null;
  }
  private embeddedEvents(result: AttendanceResultRecord): AttendanceEventRecord[] {
    const raw = result.rawPayload as Record<string, unknown> | null | undefined;
    for (const key of ['events', 'attendanceEvents', 'resolvedEvents']) {
      const value = raw?.[key]; if (Array.isArray(value)) return value.filter((item) => item && typeof item === 'object') as AttendanceEventRecord[];
    }
    return [];
  }
  private async resolveEventIds(ids: string[]): Promise<void> {
    const pending = [...new Set(ids.filter(Boolean))].filter((id) => !this.eventCache.has(id));
    for (let index = 0; index < pending.length; index += 6) {
      await Promise.all(pending.slice(index, index + 6).map(async (id) => {
        try { this.eventCache.set(id, await firstValueFrom(this.api.get<AttendanceEventRecord>(`attendance/events/${id}`))); }
        catch { this.eventCache.set(id, null); }
      }));
    }
  }
  private weekBounds(): { from: string; to: string } {
    const selected = this.parseDate(this.filters.weekDate) || new Date(); const monday = new Date(selected);
    monday.setHours(0, 0, 0, 0); monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
    return { from: this.toDateValue(monday), to: this.toDateValue(sunday) };
  }
  private restoreState(): void {
    const state = restoreAttendanceWeekFilterState(MissingPunchComponent.FILTER_STATE_KEY);
    this.filters = { companiesRecordId: state.companiesRecordId || '', orgUnitId: state.orgUnitId || '', employeeId: state.employeeId || '', weekDate: state.weekDate || this.filters.weekDate };
    this.employeeSearchText = state.employeeSearchText || ''; this.currentPage = state.currentPage || 1; this.pageSize = state.pageSize || this.pageSize;
  }
  private persistState(): void { persistAttendanceWeekFilterState(MissingPunchComponent.FILTER_STATE_KEY,
    { ...this.filters, employeeSearchText: this.displayEmployee(this.employeeSearchText), currentPage: this.currentPage, pageSize: this.pageSize }); }
  private async loadCompanies(): Promise<void> {
    try { const response = await firstValueFrom(this.api.get<CompaniesRecord[] | CollectionResponse<CompaniesRecord>>('companies-records', { params: { isActive: 'true' } }));
      this.companies = this.extract(response, 'companiesRecords').filter((item) => item.isActive !== false).sort((a, b) => a.name.localeCompare(b.name));
      this.filters.companiesRecordId = this.companySelection.resolveSharedSelection(this.companies, this.filters.companiesRecordId); } catch { this.companies = []; }
  }
  private async loadOrgUnits(): Promise<void> {
    try { const response = await firstValueFrom(this.api.get<OrgUnitRecord[] | CollectionResponse<OrgUnitRecord>>('org-units'));
      this.orgUnits = this.extract(response, 'orgUnits').filter((item) => item.isActive !== false && (!this.filters.companiesRecordId || item.companiesRecordId === this.filters.companiesRecordId)).sort((a, b) => a.name.localeCompare(b.name)); } catch { this.orgUnits = []; }
  }
  private async loadEmployees(search = ''): Promise<void> {
    try { const selected = this.selectedEmployee; const response = await firstValueFrom(this.api.get<EmployeeRecord[] | CollectionResponse<EmployeeRecord>>('employees', { params: {
        page: 1, perPage: 30, isActive: 'true', ...(search ? { search } : {}), ...(this.filters.companiesRecordId ? { companiesRecordId: this.filters.companiesRecordId } : {}), ...(this.filters.orgUnitId ? { orgUnitId: this.filters.orgUnitId } : {}) } }));
      const items = this.extract(response, 'employees').filter((item) => item.isActive !== false).sort((a, b) => a.fullName.localeCompare(b.fullName));
      this.employees = selected && !items.some((item) => item.employeeId === selected.employeeId) ? [selected, ...items] : items; } catch { this.employees = []; }
  }
  private extract<T>(response: T[] | CollectionResponse<T>, key: 'companiesRecords' | 'orgUnits' | 'employees'): T[] { return Array.isArray(response) ? response : response[key] || response.data || response.items || response.results || []; }
  private formatDate(value: string): string { const date = this.parseDate(value); return date ? new Intl.DateTimeFormat(this.locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date) : value; }
  workDate(row: MissingPunchRow): string { return this.formatDate(row.result.workDate); }
  private get locale(): string { return this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX'; }
  private parseDate(value: string): Date | null { const [year, month, day] = value.split('-').map(Number); return year && month && day ? new Date(year, month - 1, day) : null; }
  private toDateValue(date: Date): string { return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}-${`${date.getDate()}`.padStart(2, '0')}`; }
  private t(key: string): string { return this.translate.instant(key); }
}
